<template>
  <section class="payment-section">
    <h4 class="section-title">Pagamentos</h4>

    <div v-if="paymentMethodOptions.length" class="payment-field">
      <label class="payment-field__label" for="paymentMethod">Forma de pagamento</label>
      <v-select
        id="paymentMethod"
        :options="paymentMethodOptions"
        label="label"
        :reduce="(option) => option.method"
        :model-value="selectedMethod"
        placeholder="Selecione a forma de pagamento"
        @update:model-value="$emit('update:paymentMethod', $event)"
      />
    </div>

    <div class="payment-summary">
      <div class="payment-summary__row">
        <span class="payment-summary__label">Total</span>
        <span class="payment-summary__value">{{ formatBrl(total) }}</span>
      </div>

      <div class="payment-summary__row payment-summary__row--editable">
        <label class="payment-summary__label" for="paid">Pago</label>
        <CurrencyInput
          id="paid"
          :model-value="paid"
          class="payment-summary__input"
          aria-label="Valor pago"
          @update:model-value="$emit('update:paid', $event)"
        />
      </div>

      <div v-if="showFeeRow" class="payment-summary__row">
        <span class="payment-summary__label">Taxa ({{ formatFeePercent(effectiveFeePercent) }}%)</span>
        <span class="payment-summary__value payment-summary__value--fee">− {{ formatBrl(feeAmount) }}</span>
      </div>

      <div v-if="showNetRow" class="payment-summary__row payment-summary__row--total">
        <span class="payment-summary__label">Líquido</span>
        <span class="payment-summary__value payment-summary__value--net">{{ formatBrl(netAmount) }}</span>
      </div>
    </div>
  </section>
</template>

<script>
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import CurrencyInput from '@/components/CurrencyInput.vue';
import {
  getFeeAmount,
  getNetPaidAmount,
  formatFeePercent,
} from '@/composables/usePaymentMethodCalculations';

export default {
  name: 'AppointmentPaymentFields',
  components: { vSelect, CurrencyInput },
  props: {
    paid: {
      type: [String, Number, null],
      default: null,
    },
    paymentMethod: {
      type: [Object, null],
      default: null,
    },
    allPaymentMethods: {
      type: Array,
      default: () => [],
    },
    total: {
      type: Number,
      default: 0,
    },
    defaultFeePercent: {
      type: Number,
      default: 0,
    },
  },
  emits: ['update:paid', 'update:paymentMethod'],
  computed: {
    paymentMethodOptions() {
      return (this.allPaymentMethods || []).map((method) => ({
        method,
        label: `${method.name} (${formatFeePercent(method.feePercent)}%)`,
      }));
    },
    selectedMethod() {
      return this.paymentMethod || null;
    },
    effectiveFeePercent() {
      if (this.paymentMethod) {
        return Number(this.paymentMethod.feePercent) || 0;
      }
      return Number(this.defaultFeePercent) || 0;
    },
    grossPaid() {
      if (this.paid === null || this.paid === undefined || this.paid === '') return 0;
      return Number(this.paid) || 0;
    },
    feeAmount() {
      return getFeeAmount(this.grossPaid, this.effectiveFeePercent);
    },
    netAmount() {
      return getNetPaidAmount(this.grossPaid, this.effectiveFeePercent);
    },
    showFeeRow() {
      return this.grossPaid > 0 && this.feeAmount > 0;
    },
    showNetRow() {
      return this.grossPaid > 0 &&
        (this.paymentMethodOptions.length > 0 || this.effectiveFeePercent > 0);
    },
  },
  methods: {
    formatBrl(value) {
      return (Number(value) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    formatFeePercent,
  },
};
</script>

<style scoped>
.section-title {
  font-size: 0.875rem;
  font-weight: 600;
  margin: 0 0 var(--space-md);
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.payment-field {
  margin-bottom: var(--space-md);
}

.payment-field__label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-bottom: var(--space-xs);
  text-transform: none;
  letter-spacing: normal;
}

.payment-summary {
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.payment-summary__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  padding: var(--space-sm) var(--space-md);
  border-bottom: 1px solid var(--color-border);
}

.payment-summary__row:last-child {
  border-bottom: none;
}

.payment-summary__row--total {
  background: var(--color-accent-soft);
  padding-top: var(--space-md);
  padding-bottom: var(--space-md);
}

.payment-summary__label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin: 0;
  text-transform: none;
  letter-spacing: normal;
}

.payment-summary__value {
  font-size: 0.95rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--color-text);
  text-align: right;
}

.payment-summary__value--fee {
  font-weight: 500;
  color: var(--color-text-muted);
}

.payment-summary__value--net {
  font-size: 1rem;
  color: var(--color-accent);
}

.payment-summary__input {
  width: 7.5rem;
  flex-shrink: 0;
}

.payment-summary__input :deep(.currency-input-wrap) {
  min-height: 36px;
}

.payment-summary__input :deep(.currency-prefix) {
  padding: 6px 0 6px 10px;
  font-size: 0.875rem;
}

.payment-summary__input :deep(.currency-input) {
  padding: 6px 10px 6px 4px;
  font-size: 0.875rem;
  font-weight: 600;
  text-align: right;
}
</style>
